// Orion Archive Index (Step 4b) — admin-only registered-entry ledger route.
//
// Doctrinal posture (see audit note):
//   - admin-only, ssr: false, preload: false
//   - mechanical OFFSET/LIMIT pagination via ?page=N only
//   - URL is normalized to the clamped canonical page (never silently
//     mismatched)
//   - empty deep pages walk back to the nearest non-empty page, then the
//     URL is normalized to that canonical page
//   - no prefetch, no preload, no optimistic traversal
//   - no row click target, no keyboard traversal

import { useEffect } from "react";
import {
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { fallback, zodValidator } from "@tanstack/zod-adapter";
import { supabase } from "@/integrations/supabase/client";
import { listRegisteredEntriesFn } from "@/lib/archive/index.functions";
import { RegisteredEntryIndex } from "@/components/archive/RegisteredEntryIndex";
import { ArchiveStateBanner } from "@/components/archive/ArchiveStateBanner";

const MAX_PAGE = 100;

const searchSchema = z.object({
  page: fallback(z.number().int().min(1).max(MAX_PAGE), 1).default(1),
});

export const Route = createFileRoute("/_authenticated/archive/")({
  ssr: false,
  preload: false,
  staleTime: 0,
  validateSearch: zodValidator(searchSchema),
  beforeLoad: async () => {
    const { data: sess } = await supabase.auth.getSession();
    const userId = sess.session?.user.id;
    if (!userId) {
      throw redirect({ to: "/auth", search: { tab: "login" } });
    }
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (error || !data) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: ArchiveIndexRoute,
});

function ArchiveIndexRoute() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const fetchList = useServerFn(listRegisteredEntriesFn);

  const { data, isLoading, error } = useQuery({
    queryKey: ["orion-archive-index", search.page],
    queryFn: () => fetchList({ data: { page: search.page } }),
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });

  // Deterministic URL normalization. If the server clamped the page (out
  // of range) or walked back (empty deep page), replace the URL with the
  // canonical page. No fuzzy recovery; one mechanical replace.
  useEffect(() => {
    if (!data) return;
    if (data.canonical_page !== search.page) {
      navigate({
        to: "/archive",
        search: { page: data.canonical_page },
        replace: true,
      });
    }
  }, [data, search.page, navigate]);

  return (
    <main className="mx-auto max-w-[1100px] px-6 py-8">
      <div className="space-y-4">
        <ArchiveStateBanner />

        {isLoading ? (
          <div className="border border-border/60 bg-card/40 px-4 py-3 font-mono text-xs text-muted-foreground">
            loading...
          </div>
        ) : null}

        {error ? (
          <div className="border border-border/60 bg-card/40 px-4 py-3 font-mono text-xs text-destructive">
            {error instanceof Error ? error.message : String(error)}
          </div>
        ) : null}

        {data ? <RegisteredEntryIndex result={data} /> : null}
      </div>
    </main>
  );
}
